import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  IconButton,
  InputAdornment
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { Ramo } from '../../types';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';

type CampoCuenta = 'ctaInventario' | 'ctaVenta' | 'ctaCompra' | 'ctaCostoVenta' | 'ctaDesc' | 'ctaCtraPartida';

const CAMPOS_CUENTA: { key: CampoCuenta; label: string }[] = [
  { key: 'ctaInventario', label: 'Cuenta Inventario' },
  { key: 'ctaVenta', label: 'Cuenta Venta' },
  { key: 'ctaCompra', label: 'Cuenta Compra' },
  { key: 'ctaCostoVenta', label: 'Cuenta Costo Venta' },
  { key: 'ctaDesc', label: 'Cuenta Descuento' },
  { key: 'ctaCtraPartida', label: 'Cuenta Contrapartida' },
];

const RamoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoRamo } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imputableSearchCampo, setImputableSearchCampo] = useState<CampoCuenta | null>(null);
  const [imputablesCuentas, setImputablesCuentas] = useState<Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null>>({
    ctaInventario: null,
    ctaVenta: null,
    ctaCompra: null,
    ctaCostoVenta: null,
    ctaDesc: null,
    ctaCtraPartida: null
  });
  const [formData, setFormData] = useState<Ramo>({
    codigoRamo: '',
    nombreRamo: '',
    estadoRamo: 'ACTIVO',
    ctaInventario: 0,
    ctaVenta: 0,
    ctaCompra: 0,
    ctaCostoVenta: 0,
    ctaDesc: 0,
    ctaCtraPartida: 0,
    archCodBarra: '',
    cantDecimales: 2,
    editaPrecio: 0
  });

  const loadRamo = useCallback(async () => {
    try {
      setLoading(true);
      if (!codigoRamo) return;
      const ramo = await catalogosService.getRamo(codigoRamo);
      const estadoTransformado = ramo.estadoRamo?.trim().toUpperCase() === 'A' 
        ? 'ACTIVO' 
        : ramo.estadoRamo?.trim().toUpperCase() === 'I' 
        ? 'INACTIVO' 
        : ramo.estadoRamo || 'ACTIVO';
      setFormData({
        ...ramo,
        estadoRamo: estadoTransformado
      });

      const nuevasEtiquetas: Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null> = {
        ctaInventario: null,
        ctaVenta: null,
        ctaCompra: null,
        ctaCostoVenta: null,
        ctaDesc: null,
        ctaCtraPartida: null
      };
      await Promise.all(
        CAMPOS_CUENTA.map(async ({ key }) => {
          const nro = ramo[key];
          if (nro != null && Number(nro) > 0) {
            try {
              const imputable = await imputableService.getImputable(Number(nro));
              if (imputable) {
                nuevasEtiquetas[key] = { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() };
              }
            } catch {
              // si falla, se mostrará solo el número
            }
          }
        })
      );
      setImputablesCuentas(nuevasEtiquetas);
    } catch (error) {
      console.error('Error al cargar el ramo:', error);
      enqueueSnackbar('Error al cargar el ramo', { variant: 'error' });
      navigate('/inventario/ramos');
    } finally {
      setLoading(false);
    }
  }, [codigoRamo, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoRamo) {
      loadRamo();
    }
  }, [codigoRamo, loadRamo]);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'estadoRamo' 
        ? (checked ? 'ACTIVO' : 'INACTIVO')
        : (checked ? 1 : 0)
    }));
  };

  const abrirBuscadorImputable = (campo: CampoCuenta) => {
    setImputableSearchCampo(campo);
  };

  const valorDisplayCuenta = (campo: CampoCuenta): string => {
    const sel = imputablesCuentas[campo];
    if (sel) return `${sel.nroCuenta} - ${sel.nomCuenta || ''}`;
    const num = formData[campo];
    return num != null && num !== 0 ? String(num) : '';
  };

  const handleImputableSelect = (imputable: Imputable) => {
    if (imputableSearchCampo) {
      setFormData(prev => ({
        ...prev,
        [imputableSearchCampo]: imputable.nroCuenta
      }));
      setImputablesCuentas(prev => ({
        ...prev,
        [imputableSearchCampo]: { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || imputable.descripcioncta || '').trim() }
      }));
      setImputableSearchCampo(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      // Transformar el estado del formulario ('ACTIVO'/'INACTIVO') al formato del backend ('A'/'I')
      const estadoBackend = formData.estadoRamo === 'ACTIVO' ? 'A' : 'I';
      const ramoData = {
        ...formData,
        estadoRamo: estadoBackend,
        ctaInventario: Number(formData.ctaInventario) || 0,
        ctaVenta: Number(formData.ctaVenta) || 0,
        ctaCompra: Number(formData.ctaCompra) || 0,
        ctaCostoVenta: Number(formData.ctaCostoVenta) || 0,
        ctaDesc: Number(formData.ctaDesc) || 0,
        ctaCtraPartida: Number(formData.ctaCtraPartida) || 0,
        cantDecimales: Number(formData.cantDecimales) || 0,
        editaPrecio: Number(formData.editaPrecio) || 0
      };

      if (codigoRamo) {
        await catalogosService.updateRamo(codigoRamo, ramoData);
      } else {
        await catalogosService.createRamo(ramoData);
      }
      
      enqueueSnackbar('Ramo guardado exitosamente', { variant: 'success' });
      navigate('/inventario/ramos');
    } catch (error) {
      console.error('Error al guardar el ramo:', error);
      enqueueSnackbar('Error al guardar el ramo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {codigoRamo ? 'Editar Ramo' : 'Nuevo Ramo'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="codigoRamo"
                  label="Código"
                  value={formData.codigoRamo}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  disabled={!!codigoRamo}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="nombreRamo"
                  label="Nombre"
                  value={formData.nombreRamo}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              {CAMPOS_CUENTA.map(({ key, label }) => (
                <Grid item xs={12} md={6} key={key}>
                  <TextField
                    name={key}
                    label={label}
                    value={valorDisplayCuenta(key)}
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => abrirBuscadorImputable(key)}
                            edge="end"
                            color="primary"
                            title="Buscar cuenta contable"
                            sx={{ borderRadius: 2 }}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} md={6}>
                <TextField
                  name="archCodBarra"
                  label="Archivo Código de Barras"
                  value={formData.archCodBarra}
                  onChange={handleTextFieldChange}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="cantDecimales"
                  label="Cantidad de Decimales"
                  type="number"
                  value={formData.cantDecimales}
                  onChange={handleTextFieldChange}
                  fullWidth
                  inputProps={{ min: 0, max: 6 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.editaPrecio)}
                      onChange={handleSwitchChange}
                      name="editaPrecio"
                    />
                  }
                  label="Edita Precio"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estadoRamo === 'ACTIVO'}
                      onChange={handleSwitchChange}
                      name="estadoRamo"
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/ramos')}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ImputableSearch
        open={imputableSearchCampo !== null}
        onClose={() => setImputableSearchCampo(null)}
        onSelect={handleImputableSelect}
      />
    </Box>
  );
};

export default RamoForm; 